/**
 * Historia clínica estructurada (formato del prototipo): antecedentes, alergias, familiares y hábitos
 * como listas para marcar; signos vitales por separado; examen del sistema estomatognático por regiones
 * (formulario 033 del MSP). Lo dictado marca las casillas; también se marcan a mano.
 *
 * Compatibilidad: además de la estructura, se guardan los textos de siempre (antecedentes_personales,
 * alergias, medicacion, habitos, signos_vitales…), que usan las recetas, los consentimientos y la IA.
 */
import { norm } from "./texto.ts";
import { normalizarIndicadores, type IndicadoresBucales } from "./indicadores.ts";

type Item = readonly [string, RegExp];

export const APP: readonly Item[] = [
  ["Diabetes", /diabet/], ["Hipertensión arterial", /hipertens|presion alta/],
  ["Cardiopatía", /cardiopat|infarto|arritmi|soplo|marcapaso|valvul|insuficiencia cardiaca/],
  ["Trastornos de coagulación", /hemorrag|hemofil|coagulaci|sangra mucho|sangrado excesivo/],
  ["Anticoagulantes", /warfarin|anticoagul|rivaroxab|apixab|clopidogrel|acenocumar/],
  ["Asma", /asma/], ["Epilepsia", /epilep|convulsi/], ["Hepatitis", /hepatitis/], ["VIH", /\bvih\b|\bsida\b/], ["Tuberculosis", /tuberculos/],
  ["Enfermedad renal", /renal|rinon|dialisis/], ["Hipertiroidismo", /hipertiroid/], ["Hipotiroidismo", /hipotiroid/], ["Enfermedad tiroidea", /(?<!hiper)(?<!hipo)tiroid|bocio/], ["Cáncer / radioterapia", /cancer|radioterap|quimioterap|tumor/],
  ["Osteoporosis / bifosfonatos", /osteopor|bifosfonat|alendronat|zoledron|ibandronat/], ["Embarazo", /embaraz|gestaci/], ["Lactancia", /lactan|amamant/],
  ["Gastritis / reflujo", /gastritis|reflujo/], ["Trastorno psiquiátrico", /depresi|ansiedad|bipolar|esquizofren/],
];
export const ALG: readonly Item[] = [
  ["Penicilina", /penicilin|amoxicilin|ampicilin/], ["AINEs / aspirina", /\baines?\b|ibuprofen|aspirin|diclofenac|naproxen|ketorolac/],
  ["Anestésicos locales", /anestesi|lidocain|articain|mepivacain/], ["Látex", /latex/], ["Yodo", /yodo/], ["Sulfas", /sulfa/],
  ["Clorhexidina", /clorhexidin/], ["Metamizol", /metamizol|dipirona/], ["Cefalosporinas", /cefalexin|cefalospor|cefadrox/],
];
export const FAM: readonly Item[] = [
  ["Diabetes", /diabet/], ["Hipertensión arterial", /hipertens|presion alta/], ["Cardiopatía", /cardiopat|infarto|corazon/], ["Cáncer", /cancer|tumor/],
  ["Trastornos de coagulación", /hemofil|coagulaci/], ["Enfermedad periodontal", /periodont|piorrea|perdio los dientes/],
  ["Labio o paladar fisurado", /fisura|leporino/], ["Asma", /asma/],
  ["Enf. cardiovascular", /cardiovascular|derrame|\bacv\b|trombosis|varices|embolia/], ["Hipertiroidismo", /hipertiroid/], ["Hipotiroidismo", /hipotiroid/], ["Endócrino metabólico", /endocrin|metabolic|(?<!hiper)(?<!hipo)tiroid|bocio|colesterol|trigliceridos|obesidad/],
  ["Tuberculosis", /tuberculos/], ["Enf. mental", /\bmental|psiquiatr|depresi|esquizofren|bipolar|ansiedad/], ["Enf. infecciosa", /infecciosa|hepatitis|\bvih\b|\bsida\b/], ["Mal formación", /malformaci|paladar hendido/],
];
export const HAB: readonly Item[] = [
  ["Tabaco", /fuma|tabaco|cigarr/], ["Alcohol", /alcohol|bebe licor|toma licor|cerveza/], ["Bruxismo", /bruxis|rechin|aprieta los dientes/],
  ["Onicofagia", /onicofag|(come|muerde|se come|se muerde) las unas/], ["Respiración bucal", /respira(cion)? (por la )?boca|respirador bucal|respiracion bucal/],
  ["Succión digital", /chupa el dedo|succion digital|se chupa el dedo/], ["Morder objetos", /muerde (lapices|objetos|esferos|plumas)/],
];
/** Antecedentes que cambian la atención: se destacan en rojo. */
export const APP_ALERTA = ["Diabetes", "Hipertensión arterial", "Cardiopatía", "Trastornos de coagulación", "Anticoagulantes", "Epilepsia", "Asma", "Hepatitis", "VIH",
  "Enfermedad renal", "Cáncer / radioterapia", "Osteoporosis / bifosfonatos", "Embarazo", "Lactancia"];

export interface Region { k: string; n: string; o: readonly (readonly [string, string, RegExp])[]; rx: RegExp }
export const REGIONES: readonly Region[] = [
  { k: "labios", n: "Labios", o: [["Queilitis", "K13.0", /queilitis|boquera|comisura/], ["Herpes labial", "B00.1", /herpes/], ["Mucocele", "K11.6", /mucocele/], ["Resequedad / descamación", "K13.0", /reseco|descam|agrietad/]], rx: /labio/ },
  { k: "mejillas", n: "Mejillas", o: [["Asimetría / aumento de volumen", "R22.0", /asimetr|aumento de volumen|edema|hinchaz/], ["Celulitis facial", "L03.2", /celulitis/]], rx: /mejilla|\bcara\b|facial/ },
  { k: "max_sup", n: "Maxilar superior", o: [["Exostosis / torus", "K10.0", /torus|exostosis/], ["Quiste", "K09.2", /quiste/], ["Alveolitis", "K10.3", /alveolitis/], ["Proceso inflamatorio", "K10.2", /osteitis|osteomielitis|inflamatori/]], rx: /maxilar superior/ },
  { k: "max_inf", n: "Maxilar inferior", o: [["Torus mandibular / exostosis", "K10.0", /torus|exostosis/], ["Quiste", "K09.2", /quiste/], ["Alveolitis", "K10.3", /alveolitis/], ["Proceso inflamatorio", "K10.2", /osteitis|osteomielitis|inflamatori/]], rx: /maxilar inferior|(?<!sub|temporo)mandibul/ },
  { k: "lengua", n: "Lengua", o: [["Lengua saburral / vellosa", "K14.3", /saburral|vellosa/], ["Lengua geográfica", "K14.1", /geografica/], ["Lengua fisurada", "K14.5", /fisurada|plegada/], ["Glositis", "K14.0", /glositis/], ["Anquiloglosia", "Q38.1", /anquilogl|frenillo lingual corto/], ["Candidiasis", "B37.0", /candid|muguet/]], rx: /lengua/ },
  { k: "paladar", n: "Paladar", o: [["Torus palatino", "K10.0", /torus/], ["Estomatitis protésica", "B37.0", /protesic|candid/], ["Estomatitis nicotínica", "K13.2", /nicotin/], ["Fisura palatina", "Q35.9", /fisura/]], rx: /paladar/ },
  { k: "piso", n: "Piso de boca", o: [["Ránula", "K11.6", /ranula/], ["Sialolito", "K11.5", /sialolit|calculo salival/], ["Aumento de volumen", "R22.0", /aumento de volumen|edema/]], rx: /piso de (la )?boca/ },
  { k: "carrillos", n: "Carrillos", o: [["Línea alba / mordisqueo", "K13.1", /linea alba|mordisque|morsicat/], ["Leucoplasia", "K13.2", /leucoplasia|placa blanca/], ["Úlcera aftosa", "K12.0", /afta|ulcer/], ["Fibroma traumático", "K13.4", /fibroma/], ["Liquen plano", "L43.9", /liquen/]], rx: /carrillo|mucosa yugal|mucosa bucal/ },
  { k: "glandulas", n: "Glándulas salivales", o: [["Xerostomía", "R68.2", /xerostom|boca seca|poca saliva/], ["Sialoadenitis", "K11.2", /sialoadenitis|parotiditis/], ["Sialolitiasis", "K11.5", /sialolit/], ["Hipertrofia", "K11.1", /hipertrofia/]], rx: /glandula|saliva|parotida/ },
  { k: "orofaringe", n: "Orofaringe", o: [["Faringitis", "J02.9", /faringitis|faringe (roja|eritemat)/], ["Amigdalitis", "J03.9", /amigdalitis/], ["Hipertrofia amigdalina", "J35.1", /hipertrofia amigdal|amigdalas (grandes|hipertrof)/]], rx: /orofaringe|faringe|amigdal/ },
  { k: "atm", n: "ATM", o: [["Chasquido / ruido articular", "K07.6", /chasquid|click|crepit|ruido|truena/], ["Dolor a la palpación", "K07.6", /dolor/], ["Desviación en apertura", "K07.6", /desvi/], ["Limitación de apertura", "K07.6", /limitaci|trismus/]], rx: /\batm\b|articulacion temporo|temporomandib/ },
  { k: "ganglios", n: "Ganglios", o: [["Adenopatía palpable", "R59.0", /adenopat|palpable|aumentad|adenomegal/], ["Linfadenitis dolorosa", "L04.0", /linfadenitis|doloros/]], rx: /ganglio|linfatic|submandibular|cervical/ },
  { k: "otros", n: "Otros", o: [], rx: /(?!)/ },
];

export interface EstadoRegion { e: "SP" | "CP" | null; h: string[]; nota: string }
export interface Signos { pa_s: string; pa_d: string; fc: string; fr: string; temp: string }
export interface AnamnesisEstructurada {
  motivo: string; enfermedad_actual: string;
  app: string[]; app_det: string;
  alg: string[]; alg_niega: boolean; alg_otra: string;
  medicacion: string;
  fam: string[]; fam_det: string;
  hab: string[]; hab_det: string; cepillado: "" | "0" | "1" | "2" | "3"; hilo: boolean; enjuague: boolean;
  /** casilla «Embarazada» del formulario 033 (SI / NO); vacío si no se ha preguntado */
  embarazada: "" | "si" | "no";
}
/** Pedido e informe de exámenes complementarios (secciones L y M del 033) y si cada diagnóstico es presuntivo o definitivo (sección N), por código CIE-10. */
export const TIPOS_INFORME = ["Biometría", "Química sanguínea", "Rayos X", "Otros"] as const;
/** Exámenes que se pueden pedir con una casilla (sección L del 033). */
export const EXAMENES_PEDIDO = ["Biometría hemática", "Química sanguínea", "Glucosa en ayunas", "Tiempos de coagulación (TP, TTP, INR)", "Radiografía periapical", "Radiografía panorámica", "Aleta de mordida", "Tomografía cone beam", "Cultivo y antibiograma", "Biopsia / estudio histopatológico"] as const;
export interface Complementarios { pedido_lista: string[]; pedido: string; informe_tipos: string[]; informe_otros: string; informe: string }
export const complementariosVacios = (): Complementarios => ({ pedido_lista: [], pedido: "", informe_tipos: [], informe_otros: "", informe: "" });
export type EstadoDx = "PRE" | "DEF";
export interface ExamenEstructurado { signos: Signos; regiones: Record<string, EstadoRegion>; apertura: string; periodontal: string; indicadores: IndicadoresBucales; complementarios: Complementarios; dx_estado: Record<string, EstadoDx> }
export interface HistoriaEstructurada { anamnesis: AnamnesisEstructurada; examen: ExamenEstructurado }

export const signosVacios = (): Signos => ({ pa_s: "", pa_d: "", fc: "", fr: "", temp: "" });
export const examenVacio = (): Record<string, EstadoRegion> => Object.fromEntries(REGIONES.map((r) => [r.k, { e: null, h: [], nota: "" }]));

/* ---------------- lectura de texto ---------------- */

/** Qué elementos de la lista menciona el texto (ignorando los negados: «niega diabetes»). */
export function marcar(texto: string, lista: readonly Item[]): string[] {
  const c = norm(texto);
  return lista.filter(([, re]) => {
    const m = c.match(re);
    if (!m) return false;
    const antes = c.slice(Math.max(0, m.index! - 22), m.index);
    return !/\b(niega|no refiere|sin|no tiene|no padece|no presenta)\b[^.;]*$/.test(antes) || /\b(pero|aunque|excepto)\b/.test(antes);
  }).map(([n]) => n);
}

/** ¿Dice algo más que los nombres de la lista? (p. ej. «hipertensión controlada desde 2020»). */
export function tieneDetalle(texto: string, lista: readonly Item[]): boolean {
  let c = norm(texto);
  for (const [, re] of lista) c = c.replace(new RegExp(re.source, "g"), " ");
  c = c.replace(/\b(antecedentes?|patologicos?|personales|familiares|refiere|presenta|padece|tiene|es|con|de|del|la|el|los|las|y|e|a|al|alergi[ao]s?|alergic[oa]|habitos?|arterial|mellitus|tipo|la|una|un|niega|conocidas?|\w)\b/g, " ")
    .replace(/[^a-z0-9ñ]+/g, " ").trim();
  return c.length > 3;
}

const NUM: Record<string, string> = { una: "1", un: "1", uno: "1", dos: "2", tres: "3", cuatro: "3", cinco: "3" };
export function higieneDe(texto: string): Partial<Pick<AnamnesisEstructurada, "cepillado" | "hilo" | "enjuague">> {
  const c = norm(texto), out: Partial<Pick<AnamnesisEstructurada, "cepillado" | "hilo" | "enjuague">> = {};
  if (/no se cepilla|nunca se cepilla|no cepilla/.test(c)) out.cepillado = "0";
  else {
    const m = c.match(/cepill\w*\s+(?:los dientes\s+)?(\d|una|un|uno|dos|tres|cuatro|cinco)\s+(?:vez|veces)/) ?? c.match(/(\d|una|dos|tres|cuatro|cinco)\s+(?:vez|veces)\s+al dia/);
    if (m) { const n = /^\d$/.test(m[1]) ? Math.min(3, Number(m[1])) : Number(NUM[m[1]] ?? 1); out.cepillado = String(n) as "1" | "2" | "3"; }
    else if (/despues de cada comida/.test(c)) out.cepillado = "3";
  }
  if (/(hilo|seda) dental/.test(c)) out.hilo = !/\bno (usa|utiliza|ocupa) (el )?(hilo|seda)|sin (hilo|seda)|nunca (usa )?(hilo|seda)/.test(c);
  if (/enjuague|colutorio/.test(c)) out.enjuague = !/\bno (usa|utiliza|ocupa) (el )?(enjuague|colutorio)|sin enjuague/.test(c);
  return out;
}

export function signosDe(texto: string): Partial<Signos> {
  const c = norm(texto).replace(/,(\d)/g, ".$1"), out: Partial<Signos> = {};
  const pa = c.match(/(?:presion(?: arterial)?|tension(?: arterial)?|\bpa\b|\bta\b)\s*(?:de|:)?\s*(\d{2,3})\s*(?:\/|sobre|con)\s*(\d{2,3})/) ?? c.match(/\b(\d{2,3})\s*\/\s*(\d{2,3})\b/);
  if (pa) { out.pa_s = pa[1]; out.pa_d = pa[2]; }
  const fc = c.match(/(?:pulso|frecuencia cardiaca|\bfc\b)\s*(?:de|:)?\s*(\d{2,3})/); if (fc) out.fc = fc[1];
  const fr = c.match(/(?:frecuencia respiratoria|\bfr\b)\s*(?:de|:)?\s*(\d{1,2})/); if (fr) out.fr = fr[1];
  const t = c.match(/(?:temperatura|\btemp\b|\bt\b)\s*(?:de|:)?\s*(\d{2}(?:\.\d)?)/); if (t) out.temp = t[1];
  return out;
}

/** Examen por regiones desde el dictado (reglas locales; la IA es opcional). */
export function interpretarExamen(texto: string): { regiones: Record<string, EstadoRegion>; resto: boolean; apertura: string | null } {
  const res: Record<string, EstadoRegion> = {};
  let resto = false, ap: string | null = null;
  for (const raw of texto.split(/(?<=[.;])\s+|\n+/).filter((x) => x.trim())) {
    const c = norm(raw);
    if (/resto (sin patologia|normal|sin alteracion)|demas (estructuras )?(normales|sin patologia)|resto (del examen )?(normal|conservado)/.test(c)) resto = true;
    const a = c.match(/apertura(?: bucal)?(?: maxima)?(?: de)?\s*(\d{2})\s*(mm|milimetros)?/); if (a) ap = a[1];
    for (const r of REGIONES) {
      if (!r.rx.test(c)) continue;
      const h = r.o.filter((o) => o[2].test(c)).map((o) => o[0]);
      if (/sin patologia|normal|sin alteracion|conservad|sin lesiones/.test(c) && !h.length) { res[r.k] = { e: "SP", h: [], nota: "" }; continue; }
      if (r.k === "atm" && !h.length && ap && !/dolor|chasquid|desvi|limit/.test(c)) { res[r.k] = { e: "SP", h: [], nota: "" }; continue; }
      res[r.k] = { e: "CP", h: [...new Set([...(res[r.k]?.h ?? []), ...h])], nota: [res[r.k]?.nota, raw.trim().replace(/^ATM:\s*/i, "")].filter(Boolean).join(" ").slice(0, 300) };
    }
  }
  return { regiones: res, resto, apertura: ap };
}

/* ---------------- avisos ---------------- */

export function avisoSignos(s: Partial<Signos>): { texto: string; nivel: "" | "warn" | "bad" } {
  const ps = Number(s.pa_s), pd = Number(s.pa_d), t = Number(String(s.temp ?? "").replace(",", "."));
  let texto = "", nivel: "" | "warn" | "bad" = "";
  if (ps && pd) {
    if (ps >= 180 || pd >= 110) { texto = "Presión en crisis hipertensiva: no realizar procedimientos electivos, valorar referencia."; nivel = "bad"; }
    else if (ps >= 140 || pd >= 90) { texto = "Presión elevada: controlar antes de anestesia con vasoconstrictor."; nivel = "warn"; }
    else if (ps < 90 || pd < 60) { texto = "Presión baja: vigilar lipotimia."; nivel = "warn"; }
    else texto = "Presión dentro de parámetros.";
  }
  if (t >= 37.8) { texto += `${texto ? " " : ""}Febril.`; nivel = nivel || "warn"; }
  const fc = Number(s.fc);
  if (fc && (fc > 100 || fc < 50)) { texto += `${texto ? " " : ""}${fc > 100 ? "Taquicardia" : "Bradicardia"}.`; nivel = nivel || "warn"; }
  return { texto, nivel };
}

export function avisosMedicacion(med: string): string[] {
  const m = norm(med), w: string[] = [];
  if (/warfarin|acenocumar|rivaroxab|apixab|dabigatr|clopidogrel|aspirina/.test(m)) w.push("antiagregante o anticoagulante: pedir INR o valoración antes de cirugía");
  if (/alendronat|zoledron|ibandronat|risedronat|denosumab/.test(m)) w.push("antirresortivo: riesgo de osteonecrosis en extracciones");
  if (/losartan|enalapril|amlodipin|valsartan|hidroclorot|captopril|nifedipin|atenolol|propranolol|carvedilol/.test(m)) w.push("antihipertensivo: tomar la presión en cada cita");
  if (/metformina|insulina|glibenclamid|glimepirid|sitagliptin/.test(m)) w.push("hipoglucemiante: confirmar que comió antes de la cita");
  if (/fenitoin|nifedipin|ciclosporin/.test(m)) w.push("puede causar agrandamiento gingival");
  return w;
}

/* ---------------- normalización y textos de compatibilidad ---------------- */

const texto = (v: unknown) => (typeof v === "string" ? v.slice(0, 4000) : "");
const lista = (v: unknown, validos: readonly Item[]) => (Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === "string" && validos.some(([n]) => n === x)))] : []);
const numero = (v: unknown, max: number, dec = false) => { const s = String(v ?? "").replace(",", ".").trim(); if (!s) return ""; const n = Number(s); return Number.isFinite(n) && n > 0 && n <= max ? (dec ? String(Math.round(n * 10) / 10) : String(Math.round(n))) : ""; };

/**
 * Convierte lo guardado (nuevo o de versiones anteriores, en texto) a la historia estructurada.
 * Nunca pierde texto: lo que no se reconoce queda en el detalle.
 */
export function normalizarHistoria(anamnesis: Record<string, unknown> | null | undefined, examen: Record<string, unknown> | null | undefined): HistoriaEstructurada {
  const a = anamnesis ?? {}, x = examen ?? {};
  const estructurada = Array.isArray(a.app) || Array.isArray(a.alg) || Array.isArray(a.fam) || Array.isArray(a.hab);
  let an: AnamnesisEstructurada;
  if (estructurada) {
    const cep = String(a.cepillado ?? "");
    an = {
      motivo: texto(a.motivo), enfermedad_actual: texto(a.enfermedad_actual),
      app: lista(a.app, APP), app_det: texto(a.app_det),
      alg: lista(a.alg, ALG), alg_niega: a.alg_niega === true, alg_otra: texto(a.alg_otra),
      medicacion: texto(a.medicacion),
      fam: lista(a.fam, FAM), fam_det: texto(a.fam_det),
      hab: lista(a.hab, HAB), hab_det: texto(a.hab_det), cepillado: (["0", "1", "2", "3"].includes(cep) ? cep : "") as AnamnesisEstructurada["cepillado"],
      hilo: a.hilo === true, enjuague: a.enjuague === true, embarazada: a.embarazada === "si" || a.embarazada === "no" ? a.embarazada : "",
    };
  } else {
    // historia en texto (versiones anteriores): se marcan las casillas y el texto queda como detalle
    const ap = texto(a.antecedentes_personales), al = texto(a.alergias), fa = texto(a.antecedentes_familiares), ha = texto(a.habitos), hi = texto(x.higiene);
    const alg = marcar(al, ALG), niega = /\b(niega|no refiere|ninguna|no conoce|sin alergias)\b/.test(norm(al)) && !alg.length;
    const h = higieneDe(hi);
    an = {
      motivo: texto(a.motivo), enfermedad_actual: texto(a.enfermedad_actual),
      app: marcar(ap, APP), app_det: ap && tieneDetalle(ap, APP) ? ap : "",
      alg, alg_niega: niega, alg_otra: !niega && al && (tieneDetalle(al, ALG) || !alg.length) ? al : "",
      medicacion: texto(a.medicacion),
      fam: marcar(fa, FAM), fam_det: fa,
      hab: marcar(ha, HAB), hab_det: ha && tieneDetalle(ha, HAB) ? ha : "",
      cepillado: h.cepillado ?? "", hilo: h.hilo ?? false, enjuague: h.enjuague ?? false, embarazada: "",
    };
  }
  const sg = (x.signos && typeof x.signos === "object" ? x.signos : signosDe(texto(x.signos_vitales))) as Partial<Signos>;
  const signos: Signos = { pa_s: numero(sg.pa_s, 300), pa_d: numero(sg.pa_d, 200), fc: numero(sg.fc, 250), fr: numero(sg.fr, 80), temp: numero(sg.temp, 45, true) };
  let regiones = examenVacio(), apertura = numero(x.apertura, 90);
  if (x.regiones && typeof x.regiones === "object") {
    for (const r of REGIONES) {
      const v = (x.regiones as Record<string, Partial<EstadoRegion>>)[r.k];
      if (!v) continue;
      const e = v.e === "SP" || v.e === "CP" ? v.e : null;
      regiones[r.k] = { e, h: e === "CP" && Array.isArray(v.h) ? v.h.filter((h) => r.o.some((o) => o[0] === h)) : [], nota: e === "CP" ? texto(v.nota).slice(0, 300) : "" };
    }
  } else {
    const atm = texto(x.atm);
    const viejo = [texto(x.extraoral), texto(x.intraoral), atm && !/\batm\b/i.test(atm) ? `ATM: ${atm}` : atm].filter(Boolean).map(conPunto).join(" ");
    if (viejo) { const r = interpretarExamen(viejo); regiones = { ...regiones, ...r.regiones }; if (r.apertura && !apertura) apertura = r.apertura; }
  }
  const cm = (x.complementarios && typeof x.complementarios === "object" ? x.complementarios : {}) as Partial<Complementarios>;
  const complementarios: Complementarios = { pedido_lista: EXAMENES_PEDIDO.filter((t) => Array.isArray(cm.pedido_lista) && cm.pedido_lista.includes(t)), pedido: texto(cm.pedido), informe_tipos: TIPOS_INFORME.filter((t) => Array.isArray(cm.informe_tipos) && cm.informe_tipos.includes(t)), informe_otros: texto(cm.informe_otros).slice(0, 200), informe: texto(cm.informe) };
  const dx_estado: Record<string, EstadoDx> = {};
  if (x.dx_estado && typeof x.dx_estado === "object") for (const [k, v] of Object.entries(x.dx_estado)) if (/^[A-Z]\d{2}(\.\d{1,2})?$/.test(k) && (v === "PRE" || v === "DEF")) dx_estado[k] = v;
  return { anamnesis: an, examen: { signos, regiones, apertura, periodontal: texto(x.periodontal), indicadores: normalizarIndicadores(x.indicadores), complementarios, dx_estado } };
}

const conPunto = (s: string) => (s && !/[.!?]$/.test(s) ? s + "." : s);
const unirTextos = (...xs: string[]) => xs.map((s) => s.trim()).filter(Boolean).map(conPunto).join(" ");

/** Textos de siempre, calculados de la estructura (los usan recetas, consentimientos, impresión e IA). */
export function textosHistoria(h: HistoriaEstructurada): { anamnesis: Record<string, string>; examen: Record<string, string> } {
  const a = h.anamnesis, x = h.examen;
  const alergias = a.alg_niega && !a.alg.length && !a.alg_otra ? "Niega alergias conocidas." : unirTextos(a.alg.length ? `Alergia a ${a.alg.join(", ").toLowerCase()}` : "", a.alg_otra);
  const cep = a.cepillado === "" ? "" : a.cepillado === "0" ? "No se cepilla" : `Se cepilla ${a.cepillado === "1" ? "1 vez" : a.cepillado === "2" ? "2 veces" : "3 o más veces"} al día`;
  const higiene = unirTextos(cep, a.cepillado === "" && !a.hilo && !a.enjuague ? "" : `${a.hilo ? "Usa" : "No usa"} hilo dental`, a.enjuague ? "Usa enjuague" : "");
  const s = x.signos;
  const signos = [s.pa_s && s.pa_d ? `PA ${s.pa_s}/${s.pa_d} mmHg` : "", s.fc ? `FC ${s.fc} lpm` : "", s.fr ? `FR ${s.fr} rpm` : "", s.temp ? `T ${s.temp} °C` : ""].filter(Boolean).join(", ");
  const reg = (ks: string[]) => REGIONES.filter((r) => ks.includes(r.k)).map((r) => {
    const v = x.regiones[r.k];
    if (!v?.e) return "";
    return v.e === "SP" ? `${r.n}: sin patología` : `${r.n}: ${unirTextos(v.h.join(", "), v.nota).replace(/\.$/, "") || "con patología"}`;
  }).filter(Boolean).map(conPunto).join(" ");
  const out = {
    anamnesis: {
      motivo: a.motivo, enfermedad_actual: a.enfermedad_actual,
      antecedentes_personales: unirTextos(a.app.join(", "), a.app_det),
      antecedentes_familiares: unirTextos(a.fam.join(", "), a.fam_det),
      alergias, medicacion: a.medicacion,
      habitos: unirTextos(a.hab.join(", "), a.hab_det),
    },
    examen: {
      signos_vitales: signos, higiene,
      extraoral: reg(["labios", "mejillas", "ganglios"]),
      intraoral: reg(["max_sup", "max_inf", "lengua", "paladar", "piso", "carrillos", "glandulas", "orofaringe", "otros"]),
      atm: unirTextos(reg(["atm"]), x.apertura ? `Apertura máxima ${x.apertura} mm` : ""),
      periodontal: x.periodontal,
    },
  };
  return out;
}

/** Lo que se guarda en la base: estructura + textos de compatibilidad. */
export function paraGuardar(h: HistoriaEstructurada): { anamnesis: Record<string, unknown>; examen: Record<string, unknown> } {
  const t = textosHistoria(h);
  return { anamnesis: { ...h.anamnesis, ...t.anamnesis }, examen: { ...h.examen, ...t.examen } };
}

/* ---------------- dictado → casillas ---------------- */

const agregar = (previo: string, nuevo: string) => {
  const p = previo.trim(), n = nuevo.trim();
  if (!n) return p;
  if (!p) return n;
  return norm(p).includes(norm(n).replace(/\.$/, "")) ? p : `${conPunto(p)} ${n}`;
};
const union = (a: string[], b: string[]) => [...new Set([...a, ...b])];

/** Lo que el dictado marcaría en cada campo (para mostrarlo antes de guardar). */
export function marcasDeCampo(campo: string, t: string): string[] {
  if (campo === "antecedentes_personales") return marcar(t, APP);
  if (campo === "alergias") { const m = marcar(t, ALG); return m.length ? m : /\b(niega|no refiere|ninguna)\b/.test(norm(t)) ? ["Niega alergias conocidas"] : []; }
  if (campo === "antecedentes_familiares") return marcar(t, FAM);
  if (campo === "habitos") return marcar(t, HAB);
  if (campo === "higiene") {
    const h = higieneDe(t);
    return [h.cepillado !== undefined ? (h.cepillado === "0" ? "No se cepilla" : `Cepillado: ${h.cepillado === "3" ? "3 o más" : h.cepillado} al día`) : "", h.hilo === true ? "Usa hilo dental" : h.hilo === false ? "No usa hilo dental" : "", h.enjuague ? "Usa enjuague" : ""].filter(Boolean);
  }
  if (campo === "signos_vitales") {
    const s = signosDe(t);
    return [s.pa_s ? `PA ${s.pa_s}/${s.pa_d}` : "", s.fc ? `FC ${s.fc}` : "", s.fr ? `FR ${s.fr}` : "", s.temp ? `T ${s.temp} °C` : ""].filter(Boolean);
  }
  if (["intraoral", "extraoral", "atm"].includes(campo)) {
    const r = interpretarExamen(campo === "atm" && !/\batm\b/i.test(t) ? `ATM: ${t}` : t);
    return Object.entries(r.regiones).map(([k, v]) => `${REGIONES.find((x) => x.k === k)!.n}: ${v.e === "SP" ? "sin patología" : v.h.join(", ") || "con patología"}`);
  }
  return [];
}

/**
 * Suma a la historia lo dictado (campos de texto de la consulta): marca las casillas y agrega el detalle.
 * No desmarca nada ni borra lo que ya estaba escrito.
 */
export function aplicarDictado(h: HistoriaEstructurada, d: { anamnesis?: Record<string, string | undefined>; examen?: Record<string, string | undefined> }): HistoriaEstructurada {
  const a = { ...h.anamnesis }, x = { ...h.examen, signos: { ...h.examen.signos }, regiones: { ...h.examen.regiones } };
  const da = d.anamnesis ?? {}, de = d.examen ?? {};
  if (da.motivo) a.motivo = agregar(a.motivo, da.motivo);
  if (da.enfermedad_actual) a.enfermedad_actual = agregar(a.enfermedad_actual, da.enfermedad_actual);
  if (da.antecedentes_personales) {
    const t = da.antecedentes_personales;
    a.app = union(a.app, marcar(t, APP));
    if (tieneDetalle(t, APP)) a.app_det = agregar(a.app_det, t);
  }
  if (da.alergias) {
    const t = da.alergias, m = marcar(t, ALG);
    a.alg = union(a.alg, m);
    if (!m.length && /\b(niega|no refiere|ninguna|no conoce|sin alergias)\b/.test(norm(t))) a.alg_niega = !a.alg.length && !a.alg_otra;
    else { a.alg_niega = false; if (!m.length || tieneDetalle(t, ALG)) a.alg_otra = agregar(a.alg_otra, t); }
  }
  if (da.medicacion) a.medicacion = agregar(a.medicacion, da.medicacion);
  if (da.antecedentes_familiares) { a.fam = union(a.fam, marcar(da.antecedentes_familiares, FAM)); a.fam_det = agregar(a.fam_det, da.antecedentes_familiares); }
  if (da.habitos) {
    a.hab = union(a.hab, marcar(da.habitos, HAB));
    if (tieneDetalle(da.habitos, HAB)) a.hab_det = agregar(a.hab_det, da.habitos);
    Object.assign(a, higieneDe(da.habitos));
  }
  if (de.higiene) Object.assign(a, higieneDe(de.higiene));
  if (de.signos_vitales) for (const [k, v] of Object.entries(signosDe(de.signos_vitales))) if (v) x.signos[k as keyof Signos] = v;
  const exam = [de.extraoral, de.intraoral, de.atm && !/\batm\b/i.test(de.atm) ? `ATM: ${de.atm}` : de.atm].filter((z): z is string => !!z).map(conPunto).join(" ");
  if (exam) {
    const r = interpretarExamen(exam);
    for (const [k, v] of Object.entries(r.regiones)) {
      const prev = x.regiones[k];
      x.regiones[k] = v.e === "CP" ? { e: "CP", h: union(prev?.e === "CP" ? prev.h : [], v.h), nota: agregar(prev?.e === "CP" ? prev.nota : "", v.nota).slice(0, 300) } : prev?.e === "CP" ? prev : v;
    }
    if (r.resto) for (const reg of REGIONES) if (!x.regiones[reg.k]?.e) x.regiones[reg.k] = { e: "SP", h: [], nota: "" };
    if (r.apertura) x.apertura = r.apertura;
  }
  if (de.periodontal) x.periodontal = agregar(x.periodontal, de.periodontal);
  return { anamnesis: a, examen: x };
}

/** Diagnósticos CIE-10 que salen del examen (regiones con patología). */
export function diagnosticosExamen(regiones: Record<string, EstadoRegion>): { region: string; cie10: string; detalle: string }[] {
  const out: { region: string; cie10: string; detalle: string }[] = [];
  for (const r of REGIONES) {
    const v = regiones[r.k];
    if (v?.e !== "CP") continue;
    const vistos = new Set<string>();
    for (const o of r.o) if (v.h.includes(o[0]) && !vistos.has(o[1])) {
      vistos.add(o[1]);
      out.push({ region: r.n, cie10: o[1], detalle: v.h.filter((h) => r.o.find((z) => z[0] === h)?.[1] === o[1]).join(", ") });
    }
  }
  return out;
}

/* ---------------- protección de datos (LOPDP) ---------------- */

export const DERECHOS = { acceso: "Acceso", rectificacion: "Rectificación", eliminacion: "Eliminación", oposicion: "Oposición", portabilidad: "Portabilidad", suspension: "Suspensión del tratamiento" } as const;
export type Derecho = keyof typeof DERECHOS;
export const FINES_DATOS = [
  ["atencion", "Atención odontológica e historia clínica (necesario para atenderle)"],
  ["recordatorios", "Recordatorios de citas por WhatsApp y correo"],
  ["promociones", "Saludos de cumpleaños y promociones"],
  ["fotos", "Fotografías clínicas"],
  ["imagenes_ia", "Análisis de mis radiografías y fotografías con inteligencia artificial"],
] as const;
export type FinDatos = (typeof FINES_DATOS)[number][0];

/** Suma días hábiles (lunes a viernes) a una fecha ISO. La LOPDP da 15 días para responder. */
export function diasHabiles(desde: string, n: number): string {
  const d = new Date(desde + "T12:00:00Z");
  let k = 0;
  while (k < n) { d.setUTCDate(d.getUTCDate() + 1); const w = d.getUTCDay(); if (w !== 0 && w !== 6) k++; }
  return d.toISOString().slice(0, 10);
}
export function habilesRestantes(hoy: string, hasta: string): number {
  if (hasta < hoy) return -1;
  const d = new Date(hoy + "T12:00:00Z"), h = new Date(hasta + "T12:00:00Z");
  let k = 0;
  while (d < h) { d.setUTCDate(d.getUTCDate() + 1); const w = d.getUTCDay(); if (w !== 0 && w !== 6) k++; }
  return k;
}

/* ---------------- alertas bajo el nombre del paciente ---------------- */

export interface AlertaPaciente { nivel: "rojo" | "amarillo" | "ok" | "pendiente"; grupo: "alergia" | "personal" | "familiar"; texto: string }

const corto = (t: string, n = 48) => { const x = t.trim().replace(/\s+/g, " ").replace(/\.$/, ""); return x.length > n ? x.slice(0, n - 1).trimEnd() + "…" : x; };
const soloNiega = (t: string) => /^(niega|no refiere|ninguno|ninguna|sin antecedentes|no)\b[^,;]*$/i.test(t.trim());

/**
 * Lo que el odontólogo debe ver al abrir la ficha: alergias y antecedentes personales en rojo,
 * antecedentes familiares en amarillo. Lo que no se ha registrado se indica como pendiente.
 */
export function alertasPaciente(a: AnamnesisEstructurada): AlertaPaciente[] {
  const out: AlertaPaciente[] = [];
  // alergias
  for (const x of a.alg) out.push({ nivel: "rojo", grupo: "alergia", texto: `Alergia: ${x}` });
  if (a.alg_otra.trim() && !soloNiega(a.alg_otra)) out.push({ nivel: "rojo", grupo: "alergia", texto: `Alergia: ${corto(a.alg_otra)}` });
  if (!out.length) out.push(a.alg_niega || soloNiega(a.alg_otra) && a.alg_otra.trim() ? { nivel: "ok", grupo: "alergia", texto: "Niega alergias" } : { nivel: "pendiente", grupo: "alergia", texto: "Alergias sin registrar" });
  // antecedentes patológicos personales
  const personales = a.app.map((x): AlertaPaciente => ({ nivel: "rojo", grupo: "personal", texto: x }));
  if (!personales.length && a.app_det.trim() && !soloNiega(a.app_det)) personales.push({ nivel: "rojo", grupo: "personal", texto: corto(a.app_det) });
  out.push(...personales);
  // antecedentes familiares
  const fam = a.fam.length ? a.fam : a.fam_det.trim() && !soloNiega(a.fam_det) ? [corto(a.fam_det, 40)] : [];
  for (const x of fam) out.push({ nivel: "amarillo", grupo: "familiar", texto: `Familiar: ${x}` });
  return out;
}
